import React, { useState } from "react";
import { Tag, Select, message, Button } from "antd";
import { CopyOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import TableBase from "../../../../components/table/TableBase";
import PayInvoiceButton from "../../../../components/PayInvoiceButton";
import { showInfo } from "helper/util";
import { updateFacturaThunk } from "../store/thunks";

const { Option } = Select;

const FacturasTable = ({
  dataSource,
  pagination,
  onRowClick,
  flashingFacturaId,
  loading,
  onRefresh,
  sortConfig,
  onSort,
  readOnly = false,
  isClientSide = false,
  onViewPDF
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [editingStatus, setEditingStatus] = useState({});
  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "green";
      case "partial":
        return "gold";
      case "unpaid":
        return "default";
      case "overdue":
        return "red";
      case "cancelled":
        return { backgroundColor: "#595959", color: "#ffffff" };
      default:
        return "default";
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return "--";
    }
    return window.CURRENCY_SYMBOL + parseFloat(amount).toFixed(2);
  };

  const copyToClipboard = (text, e) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success("Copied to clipboard");
      })
      .catch(() => {
        message.error("Failed to copy");
      });
  };

  const handleStatusChange = async (facturaId, newStatus, record) => {
    try {
      console.log("Updating status:", facturaId, newStatus, record);
      let updateData = { status: newStatus };

      if (newStatus === "paid") {
        // Mark as fully paid
        updateData.totalPaid = record.total;
      } else if (newStatus === "partial") {
        // This should not be called anymore since partial redirects to edit page
        message.error("Please use the edit page to set partial payments");
        return;
      } else if (newStatus === "unpaid" || newStatus === "overdue") {
        // Reset totalPaid for unpaid/overdue
        updateData.totalPaid = 0;
      } else if (newStatus === "cancelled") {
        // Reset totalPaid for cancelled invoices
        updateData.totalPaid = 0;
      }

      console.log("Update data:", updateData);
      const result = await dispatch(
        updateFacturaThunk({ id: facturaId, data: updateData })
      );
      console.log("Update result:", result);

      if (result.type.includes("fulfilled")) {
        message.success(`Status updated to ${newStatus}`);
        setEditingStatus((prev) => ({ ...prev, [facturaId]: false }));

        // Refresh the table data
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      console.error("Status update error:", error);
      message.error("Failed to update status: " + (error.message || "Unknown error"));
    }
  };

  const StatusEditor = ({ record }) => {
    const isEditing = editingStatus[record.id];

    // If readOnly, just display the status without click functionality
    if (readOnly || !isEditing) {
      const statusColor = getStatusColor(record.status);
      const tagStyle = {
        textTransform: "capitalize",
        cursor: readOnly ? "default" : "pointer",
        ...(typeof statusColor === "object" ? statusColor : {})
      };

      return (
        <Tag
          color={typeof statusColor === "string" ? statusColor : undefined}
          style={tagStyle}
          onClick={
            readOnly
              ? undefined
              : (e) => {
                  e.stopPropagation();
                  setEditingStatus((prev) => ({ ...prev, [record.id]: true }));
                }
          }
        >
          {record.status}
        </Tag>
      );
    }

    return (
      <div
        style={{ display: "flex", flexDirection: "column", gap: 4 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Select
          value={record.status}
          style={{ width: 100 }}
          onChange={(value) => {
            if (value === "partial") {
              // Redirect to edit page for partial status with special parameter
              setEditingStatus((prev) => ({ ...prev, [record.id]: false }));
              history.push(`/admin/facturas/edit/${record.id}?fromPartial=true`);
            } else {
              handleStatusChange(record.id, value, record);
            }
          }}
          onBlur={() => setEditingStatus((prev) => ({ ...prev, [record.id]: false }))}
          onClick={(e) => e.stopPropagation()}
        >
          <Option value="unpaid">Unpaid</Option>
          <Option value="partial">Partial</Option>
          <Option value="paid">Paid</Option>
          <Option value="overdue">Overdue</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
      </div>
    );
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span style={{ color: "#ccc" }}>⇅</span>;
    }
    return sortConfig.direction === "ascending" ? <span>↑</span> : <span>↓</span>;
  };

  const columns = [
    {
      title: (
        <div
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => onSort && onSort("facturaDate")}
        >
          Date {getSortIcon("facturaDate")}
        </div>
      ),
      dataIndex: "facturaDate",
      width: 120,
      render: (value) => {
        const date = new Date(value);
        return date.toISOString().split("T")[0];
      }
    },
    {
      title: (
        <div
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => onSort && onSort("facturaId")}
        >
          Invoice ID {getSortIcon("facturaId")}
        </div>
      ),
      dataIndex: "facturaId",
      width: 180,
      render: (value) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontFamily: "monospace", fontSize: "12px" }}>{value}</span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={(e) => copyToClipboard(value, e)}
            style={{ padding: 0, minWidth: "auto", height: "auto", fontSize: "10px" }}
          />
        </div>
      )
    },
    {
      title: (
        <div
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => onSort && onSort("status")}
        >
          Status {getSortIcon("status")}
        </div>
      ),
      dataIndex: "status",
      width: 80,
      render: (_, record) => <StatusEditor record={record} />
    },
    {
      title: (
        <div
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => onSort && onSort("total")}
        >
          Total {getSortIcon("total")}
        </div>
      ),
      dataIndex: "total",
      width: 80,
      align: "right",
      render: formatCurrency
    },
    {
      title: (
        <div
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => onSort && onSort("totalPaid")}
        >
          Paid {getSortIcon("totalPaid")}
        </div>
      ),
      dataIndex: "totalPaid",
      width: 80,
      align: "right",
      render: formatCurrency
    },
    {
      title: (
        <div
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => onSort && onSort("facturaType")}
        >
          Type {getSortIcon("facturaType")}
        </div>
      ),
      dataIndex: "facturaType",
      // Make type column flexible width
      render: (value) => value || "Manual"
    },
    // Only show Client column for admin users (non-readOnly mode)
    ...(!readOnly
      ? [
          {
            title: "Client",
            dataIndex: "client",
            render: (value) => showInfo(value?.clientName)
          }
        ]
      : []),
    // Add Pay Invoice column
    {
      title: "Actions",
      width: 150,
      align: "center",
      render: (_, record) => (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ display: "flex", gap: 4, justifyContent: "center" }}
        >
          {record.status !== "cancelled" && (
            <PayInvoiceButton
              facturaId={record.id}
              facturaStatus={record.status}
              isClientSide={isClientSide}
              size="small"
              onSuccess={() => {
                message.success("Payment processed successfully");
                if (onRefresh) {
                  onRefresh();
                }
              }}
              onError={(error) => {
                console.error("Payment error:", error);
              }}
            />
          )}
          {onViewPDF && (
            <Button
              type="primary"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onViewPDF(record.id);
              }}
              title="View PDF"
            >
              PDF
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="facturas-table-container">
      <TableBase
        columns={columns}
        data={dataSource}
        pagination={pagination}
        rowKey="id"
        loading={loading}
        tableLayout="fixed"
        size="small"
        className="facturas-compact-table"
        onRow={(record) => ({
          onClick: () => onRowClick && onRowClick(record.id),
          style: { cursor: "pointer" },
          className: flashingFacturaId === record.id ? "flashing-row" : ""
        })}
      />
    </div>
  );
};

export default FacturasTable;
